import { useState, useRef, useEffect, ReactNode } from "react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ReactComponent as TwoCalendarIcon } from "assets/morning/icon/TwoCalenderIcon.svg";

const formatDate = (date: Date | undefined) => {
    return date instanceof Date && !isNaN(date.getTime()) ? format(date, "yyyy. MM. dd") : "";
};

const parseDate = (input: string): Date | undefined => {
    const cleanedInput = input.replace(/\D/g, "");
    if (cleanedInput.length === 8) {
        const year = parseInt(cleanedInput.substring(0, 4), 10);
        const month = parseInt(cleanedInput.substring(4, 6), 10) - 1;
        const day = parseInt(cleanedInput.substring(6, 8), 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? undefined : date;
    }
    return undefined;
};

const formatInput = (input: string): string => {
    const cleanedInput = input.replace(/\D/g, "");
    let formatted = cleanedInput;

    if (cleanedInput.length > 4) {
        formatted = `${cleanedInput.substring(0, 4)}. ${cleanedInput.substring(4)}`;
    }
    if (cleanedInput.length > 6) {
        formatted = `${cleanedInput.substring(0, 4)}. ${cleanedInput.substring(4, 6)}. ${cleanedInput.substring(6)}`;
    }

    return formatted;
};

interface DayPickerInputProps {
    className?: string;
    initialDate?: Date;
    twoCalendar?: boolean;
    onDateChange?: (date: Date | undefined) => void;
    right?: boolean;
    disabled?: boolean;
    minDate?: Date;
    maxDate?: Date;
    icon?: ReactNode;
}
const DayPickerInputMobile = ({
    className,
    initialDate,
    twoCalendar,
    onDateChange,
    right,
    disabled,
    maxDate,
    minDate,
    icon = <TwoCalendarIcon />,
}: DayPickerInputProps) => {
    const previousDateRef = useRef<Date | undefined>(undefined);

    const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
    const [inputValue, setInputValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    const open = () => {
        setIsOpen(true);
        requestAnimationFrame(() => setIsVisible(true)); // 다음 프레임에 슬라이드 업
    };

    const close = () => {
        setIsVisible(false);
        setTimeout(() => setIsOpen(false), 300); // 트랜지션 끝나고 언마운트
    };

    const handleDateChange = (date: Date | undefined) => {
        if (date) {
            if (minDate && date < minDate) return;
            if (maxDate && date > maxDate) return;
        }
        setSelectedDate(date);
        setInputValue(formatDate(date));
        onDateChange?.(date);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { selectionStart } = e.target;
        const rawValue = e.target.value;
        const formattedValue = formatInput(rawValue);

        setInputValue(formattedValue);
        const parsedDate = parseDate(rawValue);
        if (parsedDate) {
            setSelectedDate(parsedDate);
            onDateChange?.(parsedDate);
        }

        requestAnimationFrame(() => {
            if (inputRef.current && selectionStart !== null) {
                let cursorPosition = selectionStart;
                const diff = formattedValue.length - rawValue.length;
                cursorPosition += diff > 0 ? diff : 0;
                inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            }
        });
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                close();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (!initialDate) {
            setSelectedDate(undefined);
            setInputValue("");
            previousDateRef.current = undefined;
        } else if (previousDateRef.current?.getTime() !== initialDate.getTime()) {
            setSelectedDate(initialDate);
            setInputValue(formatDate(initialDate));
            previousDateRef.current = initialDate;
        }
    }, [initialDate]);

    return (
        <div className={`relative w-full flex items-center ${className}`} ref={calendarRef}>
            <input
                disabled={disabled}
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder="기간"
                maxLength={13}
                className="disabled:bg-white h-full z-10 focus:outline-none rounded w-full  placeholder:text-grayicon"
            />
            <button disabled={disabled} type="button" className="mt-[2px]" onClick={() => (isOpen ? close() : open())}>
                {icon}
            </button>

            {isOpen && (
                <div
                    className={`fixed z-10 bg-white shadow-lg py-3 pr-3 pl-4 border bottom-0 left-0 right-0 rounded-[20px_20px_0_0]
                transform transition-transform duration-300
                ${isVisible ? "translate-y-0" : "translate-y-full"}`}
                >
                    <DayPicker
                        mode="single"
                        selected={selectedDate}
                        onSelect={date => handleDateChange(date as Date | undefined)}
                        locale={ko}
                        defaultMonth={selectedDate}
                        disabled={
                            [minDate ? { before: minDate } : undefined, maxDate ? { after: maxDate } : undefined].filter(Boolean) as any
                        }
                        formatters={{
                            formatCaption: month => format(month, "yyyy년 MM월", { locale: ko }),
                        }}
                        className="[&_.rdp-today]:text-[#FF7B2E] [&_.rdp-day]:px-0 [&_.rdp-day]:w-7 [&_.rdp-day]:h-7 [&_.rdp-selected_button]:bg-[#FF7B2E] [&_.rdp-selected_button]:rounded-[8px] [&_.rdp-selected_button]:border-none [&_.rdp-selected_button]:text-white flex justify-center"
                    />
                </div>
            )}
        </div>
    );
};

export default DayPickerInputMobile;
